import React, { useRef, useState, useEffect } from 'react';
import * as d3 from 'd3';
import styles from './LineChart.module.css';
import exportSVG from '../../utils/exportSVG';
import exportChartWithLegend from './exportChartwithLegend';

function mergeNearbyPoints(points, pixelThreshold, xScale) {
  if (!points.length) return [];
  const sorted = [...points].sort((a, b) => a.x - b.x);
  const merged = [];
  let bin = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const currPx = xScale(sorted[i].x);
    const binPx = xScale(bin[0].x);
    if (Math.abs(currPx - binPx) <= pixelThreshold) {
      bin.push(sorted[i]);
    } else {
      if (bin.length === 1) {
        merged.push(bin[0]);
      } else {
        const avgX = d3.mean(bin, d => d.x);
        const avgY = d3.mean(bin, d => d.y);
        const avgZ = d3.mean(bin, d => d.z);
        merged.push({ x: avgX, y: avgY, z: avgZ, merged: true, count: bin.length });
      }
      bin = [sorted[i]];
    }
  }
  if (bin.length === 1) {
    merged.push(bin[0]);
  } else {
    const avgX = d3.mean(bin, d => d.x);
    const avgY = d3.mean(bin, d => d.y);
    const avgZ = d3.mean(bin, d => d.z);
    merged.push({ x: avgX, y: avgY, z: avgZ, merged: true, count: bin.length });
  }
  return merged;
}

export default function LineChart({ data,toggle }) {
  const prevDataRef=useRef(null);
  const svgRef = useRef();
  const wrapperRef = useRef();
  const gRef = useRef();
  const yScaleRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const preDimensionsRef=useRef({width:0,height:0});
  const [tooltip, setTooltip] = useState({ visible: false });
  const [hoverY, setHoverY] = useState(null);
  const [binSize, setBinSize] = useState(30);
  const binSizeRef=useRef(30);
  const [zoomLevel, setZoomLevel] = useState(1);

  const downloadChartDataAsCSV = () => {
    if (!data || data.length === 0) return;
    
    let csvContent = `Task ID,Task Name,Round Number,Accuracy,${toggle==='precision'?'Threshold':'Precision'}\n`;
    
    data.forEach(line => {
      line.data.forEach(point => {
        csvContent += `${line.taskId},${line.taskName},${point.x},${point.y},${point.z}\n`;
      });
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'line_chart_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // exportSVG(svgRef.current)
    exportChartWithLegend(svgRef.current,data)
  };

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const sameData=JSON.stringify(prevDataRef.current)===JSON.stringify(data);
    const sameSize = 
      preDimensionsRef.current.width===dimensions.width &&
      preDimensionsRef.current.height===dimensions.height;

    const sameBinSize=binSizeRef===binSize;

    if (sameData&&sameSize&&sameBinSize) return;

    prevDataRef.current=data;
    preDimensionsRef.current=dimensions;
    binSizeRef.current=binSize;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 40, right: 40, bottom: 50, left: 80 };
    const innerWidth = dimensions.width - margin.left - margin.right;
    const innerHeight = dimensions.height - margin.top - margin.bottom;

    const defs = svg.append('defs');
    defs.append('filter')
      .attr('id', 'glow')
      .append('feGaussianBlur')
      .attr('stdDeviation', 4)
      .attr('result', 'coloredBlur');

    svg.append('clipPath')
      .attr('id', 'clip')
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', innerWidth)
      .attr('height', innerHeight);

    const container = svg
      .attr('width', dimensions.width)
      .attr('height', dimensions.height);

    const g = container.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    gRef.current = g;

    const allRounds = Array.from(new Set(data.flatMap(d => d.data.map(p => p.x)))).sort((a, b) => a - b);
    const xExtent = d3.extent(allRounds);
    const allY = data.flatMap(d => d.data.map(p => p.y));
    const yExtent = [Math.max(0, d3.min(allY) - 0.05), 1];

    const xScaleOriginal = d3.scaleLinear().domain(xExtent).range([0, innerWidth]);
    const yScaleOriginal = d3.scaleLinear().domain(yExtent).range([innerHeight, 0]);
    yScaleRef.current = yScaleOriginal;

    const drawChart = (xScale, yScale, zoomK, animate = false) => {
      g.selectAll('*').remove();

      g.append('g')
        .attr('clip-path', 'url(#clip)')
        .append('rect')
        .attr('width', innerWidth)
        .attr('height', innerHeight)
        .attr('fill', 'transparent');

      const yStart = Math.floor(yExtent[0] * 10) / 10;
      const yEnd = yExtent[1];

      for (let yVal = yStart; yVal <= yEnd + 0.0001; yVal += 0.1) {
        const y = yScale(yVal);
        g.append('line')
          .attr('x1', 0)
          .attr('x2', innerWidth)
          .attr('y1', y)
          .attr('y2', y)
          .attr('stroke', '#363636')
          .attr('stroke-width', 1.5)
          .attr('stroke-linecap', 'round')
          .attr('stroke-dasharray', '8 20');

        g.append('text')
          .text(yVal.toFixed(1))
          .attr('x', -40)
          .attr('y', y)
          .attr('fill', '#7c7d7d')
          .style('font-size', '12px')
          .style('font-weight', 'bold')
          .attr('alignment-baseline', 'middle');
      }

      const xAxis = d3.axisBottom(xScale).ticks(10);
      const xAxisG = g.append('g')
        .attr('transform', `translate(0, ${innerHeight})`)
        .call(xAxis);

      xAxisG.selectAll('text')
        .style('fill', '#7c7d7d')
        .style('font-size', '12px')
        .style('font-weight', 'bold');

      data.forEach((testLine, i) => {
        // const pixelThreshold = binSize * zoomK;
        // const mergedData = mergeNearbyPoints(testLine.data, pixelThreshold, xScale);
        const mergedData = mergeNearbyPoints(testLine.data, binSize, xScale);

        const line = d3.line()
          .x(d => xScale(d.x))
          .y(d => yScale(d.y))
          .curve(d3.curveMonotoneX);

        const pathGlow = g.append('path')
          .datum(mergedData)
          .attr('class', 'line')
          .attr('id', `line-${i}-glow`)
          .attr('fill', 'none')
          .attr('stroke', testLine.color)
          .attr('stroke-width', 2)
          .attr('filter', 'url(#glow)')
          .attr('d', line);

        const pathLine = g.append('path')
          .datum(mergedData)
          .attr('class', 'line')
          .attr('id', `line-${i}`)
          .attr('fill', 'none')
          .attr('stroke', testLine.color)
          .attr('stroke-width', 2)
          .attr('d', line);

        const pathHover = g.append('path')
          .datum(mergedData)
          .attr('class', 'line')
          .attr('id', `line-${i}-hover`)
          .attr('fill', 'none')
          .attr('stroke', 'transparent')
          .attr('stroke-width', 10)
          .on('mouseover', () => {
            g.selectAll('.line').filter(function(){
              return ![`line-${i}`,`line-${i}-glow`].includes(this?.id);
            }).transition().duration(250).attr('opacity',.1);
            g.selectAll('.dot').filter(function(){
              return !this?.classList.contains(`dot-${i}`);
            })
            .transition().duration(250).attr('opacity',.1);
          })
          .on('mousemove', (event, d) => {
            const [x, y] = d3.pointer(event);
            setTooltip({
              visible: true,
              x: x + margin.left + 20,
              y: y + margin.top - 20,
              value: "",
              z: "",
              backgroundColor: testLine.color,
              name: testLine.taskName,
              merged: "Line",
              count:"" 
            });
          })
          .on('mouseout', () => {
            data.forEach(item=>{
              g.selectAll('.line').attr('opacity',1);
              g.selectAll('.dot').attr('opacity',1);
            })
            setTooltip({ visible: false });
          })
          .attr('d', line);

        

        const dot=g.selectAll(`.dot-${i}`)
          .data(mergedData)
          .enter()
          .append('circle')
          .attr('class',`dot dot-${i}`)
          .attr('cx', d => xScale(d.x))
          .attr('cy', d => (d.y===undefined||d.y===null? yScale(1): yScale(d.y)))
          .attr('r', d => d.merged ? 6 : 4)
          .attr('fill', '#242424')
          .attr('stroke', d => (d.y===undefined||d.y===null?'#999':testLine.color))
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', d => (d.y===undefined||d.y===null?'2 4':'none'))


        g.selectAll(`.dot-${i}-hover`)
          .data(mergedData)
          .enter()
          .append('circle')
          .attr('class',`dot-hover dot-hover-${i}`)
          .attr('cx', d => xScale(d.x))
          .attr('cy', d => (d.y===undefined||d.y===null? yScale(1): yScale(d.y)))
          .attr('r', 14)
          .attr('fill', '#242424')
          .attr('stroke', d => (d.y===undefined||d.y===null?'#999':testLine.color))
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', d => (d.y===undefined||d.y===null?'2 4':'none'))
          .attr('opacity', 0)
          .on('mouseover', () => {
            g.selectAll('.line').filter(function(){
              return ![`line-${i}`,`line-${i}-glow`].includes(this?.id);
            }).transition().duration(250).attr('opacity',.1);
            g.selectAll('.dot').filter(function(){
              return !this?.classList.contains(`dot-${i}`);
            })
            .transition().duration(250).attr('opacity',.1);
          })
          .on('mousemove', (event, d) => {
            const [x, y] = d3.pointer(event);
            setTooltip({
              visible: true,
              x: x + margin.left + 20,
              y: y + margin.top - 20,
              value: d.y,
              z: d.z,
              backgroundColor: d.y===undefined||d.y===null?'#999':testLine.color,
              name: testLine.taskName,
              merged: d.merged,
              count: d.count
            });
          })
          .on('mouseout', () => {
            data.forEach(item=>{
              g.selectAll('.line').transition().duration(250).attr('opacity',1);
              g.selectAll(`[id^="line-${i}-hover"]`).attr('stroke','transparent');
              g.selectAll('.dot').transition().duration(250).attr('opacity',1);
            })
            setTooltip({ visible: false })
          })

        if (animate) {
          [pathLine, pathGlow].forEach(p => {
            p.attr('stroke-dasharray', function () {
              const length = this.getTotalLength();
              return `${length} ${length}`;
            })
              .attr('stroke-dashoffset', function () {
                return this.getTotalLength();
              })
              .transition()
              .duration(1000)
              .ease(d3.easeCubicInOut)
              .attr('stroke-dashoffset', 0);
          });
          [dot].forEach(p => {
            p.attr('r', 0)
            .transition()
            .delay((_, j) => (j * 800) / mergedData.length)
            .duration(600)
            .ease(d3.easeCubicOut)
            .attr('r', d => d.merged ? 6 : 4);
          });
        }
      }
    )
    };

    drawChart(xScaleOriginal, yScaleOriginal, zoomLevel, true);

    const overlay=svg.append('rect')
    .attr('class','chart-overlay')
    .attr('x',0)
    .attr('y',0)
    .attr('width', dimensions.width)
    .attr('height', dimensions.height)
    .attr('fill','white')
    .attr('opacity',0)
    .style('pointer-events','all')

    setTimeout(() => {
      overlay.remove();
    }, 1000);

    const zoom = d3.zoom()
      .scaleExtent([1, 10])
      .translateExtent([[0, 0], [dimensions.width, dimensions.height]])
      .on('zoom', (event) => {
        const newXScale = event.transform.rescaleX(xScaleOriginal);
        const newYScale = event.transform.rescaleY(yScaleOriginal);
        setZoomLevel(event.transform.k);
        drawChart(newXScale, newYScale, event.transform.k, false);
      });

    container.call(zoom);

    svg.on('mousemove', function (e) {
      const [_, mouseY] = d3.pointer(e);
      const chartY = mouseY - margin.top;
      if (chartY < 0 || chartY > innerHeight) {
        setHoverY(null);
        return;
      }
      const yValue = yScaleOriginal.invert(chartY);
      setHoverY(yValue);
    });

    svg.on('mouseleave', () => {
      setHoverY(null);
    });
  }, [data, dimensions, binSize]);

  useEffect(() => {
    const g = gRef.current;
    const yScale = yScaleRef.current;
    if (!g || !yScale) return;

    g.select('#hover-line')?.remove();
    g.select('#hover-label')?.remove();
    g.select('#hover-label-bg')?.remove();
    if (hoverY === null) return;

    const y = yScale(hoverY);
    const innerWidth = dimensions.width - 80;

    g.append('line')
      .attr('id', 'hover-line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', y)
      .attr('y2', y)
      .attr('stroke', '#7c7d7d')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4 4')
      .attr('pointer-events', 'none')
      .lower();

    g.append('rect')
      .attr('id', 'hover-label-bg')
      .attr('x', -60)
      .attr('y', y - 14)
      .attr('width', 50)
      .attr('height', 26)
      .attr('rx', 6)
      .attr('ry', 6)
      .style('fill', '#181818')
      .style('opacity', 0.8)
      .attr('pointer-events', 'none');

    g.append('text')
      .attr('id', 'hover-label')
      .text(hoverY.toFixed(3))
      .attr('x', -50)
      .attr('y', y)
      .attr('alignment-baseline', 'middle')
      .style('font-size', '12')
      .style('fill', '#ffffff')
      .style('font-weight', 'bold')
      .attr('pointer-events', 'none');
  }, [hoverY]);

  return (
    <div ref={wrapperRef} className={styles['wrap']}>
      <div className={styles['slider']}>
        <span>Smooth</span>
        <span>{binSize}</span>
        <input
          type="range"
          min="1"
          max="30"
          step="1"
          value={binSize}
          onChange={e => setBinSize(+e.target.value)}
        />
      </div>
      <button className={styles['export-btn']} onClick={downloadChartDataAsCSV}>Export to CSV</button>
      <svg ref={svgRef}/>
      {tooltip.visible && (
        <div className={styles['tooltip']} style={{ left: tooltip.x, top: tooltip.y }}>
          <div>
            <div className={styles['tooltip-dot']} style={{backgroundColor:tooltip.backgroundColor}}/>
            <span>{tooltip.name}</span>
          </div>
          {tooltip.merged!=='Line'&&<>
          <span>{
          tooltip.value?
          tooltip.merged ?
          `요약된 ${tooltip.count}개 성능` 
          : 
          '단일 성능'
          :
          '측정중...'
          }</span>
          {tooltip.value&&<><span>Accuracy: {tooltip.value}</span>
          <span>{toggle==='precision'?'Threshold:':'precision:'}{tooltip.z}</span>
          </>}</>}
        </div>
      )}
    </div>
  );
}